import React from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, IconButton, MenuItem, Select, Typography } from '@mui/material';

export interface PaginationData {
  page: number;
  per_page: number;
  /** Exact-count pagination (default mode used by most billing tables). */
  total?: number;
  total_pages?: number;
  /**
   * Lower-bound pagination (GDN-2142). When `has_more` is provided, the
   * paginator renders "1–N of N+" and enables Next while `has_more` is true,
   * without an exact org-wide total. `count_on_page` is the number of rows on
   * the current page; `known_minimum` is the known lower bound on the total.
   */
  has_more?: boolean;
  known_minimum?: number;
  count_on_page?: number;
}

interface PaginationProps {
  data: PaginationData;
  onPageChange: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
  perPageOptions?: number[];
  /** Compact mode: just page nav, no range text or rows selector */
  compact?: boolean;
}

const DEFAULT_PER_PAGE_OPTIONS = [25, 50, 100];

export const Pagination: React.FC<PaginationProps> = ({
  data,
  onPageChange,
  onPerPageChange,
  perPageOptions = DEFAULT_PER_PAGE_OPTIONS,
  compact = false,
}) => {
  const { page, per_page } = data;
  const isLowerBound = data.has_more !== undefined;

  let rangeText: string;
  let prevDisabled: boolean;
  let nextDisabled: boolean;
  let pageLabel: string | null;

  if (isLowerBound) {
    // Lower-bound mode: no exact total — show "start–end of known+" and let the
    // user keep paging while has_more is true.
    const countOnPage = data.count_on_page ?? 0;
    const hasMore = data.has_more ?? false;
    if (countOnPage === 0 && page <= 1) return null;
    const start = (page - 1) * per_page + 1;
    const end = (page - 1) * per_page + countOnPage;
    const known = data.known_minimum ?? end;
    rangeText = `${start}–${end} of ${known.toLocaleString()}${hasMore ? '+' : ''}`;
    prevDisabled = page <= 1;
    nextDisabled = !hasMore;
    pageLabel = null; // total page count is unknown in lower-bound mode
  } else {
    const total = data.total ?? 0;
    const totalPages = data.total_pages ?? 1;
    if (total === 0 || totalPages <= 1) return null;
    const start = (page - 1) * per_page + 1;
    const end = Math.min(page * per_page, total);
    rangeText = `${start}–${end} of ${total.toLocaleString()}`;
    prevDisabled = page <= 1;
    nextDisabled = page >= totalPages;
    pageLabel = `Page ${page} of ${totalPages}`;
  }

  if (compact) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
          {rangeText}
        </Typography>
        <IconButton
          size="small"
          disabled={prevDisabled}
          onClick={() => onPageChange(page - 1)}
          sx={{ p: 0.25 }}
        >
          <ChevronLeftIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton
          size="small"
          disabled={nextDisabled}
          onClick={() => onPageChange(page + 1)}
          sx={{ p: 0.25 }}
        >
          <ChevronRightIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pt: 1.5,
      }}
    >
      <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
        {rangeText}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {onPerPageChange && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography variant="body2" sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
              Rows
            </Typography>
            <Select
              size="small"
              value={per_page}
              onChange={e => onPerPageChange(e.target.value)}
              sx={{
                fontSize: '0.75rem',
                height: 28,
                '& .MuiSelect-select': { py: 0.25, pr: 3 },
              }}
            >
              {perPageOptions.map(n => (
                <MenuItem key={n} value={n} sx={{ fontSize: '0.75rem' }}>
                  {n}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}

        {pageLabel && (
          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', mx: 0.5 }}>
            {pageLabel}
          </Typography>
        )}

        <IconButton
          size="small"
          disabled={prevDisabled}
          onClick={() => onPageChange(page - 1)}
          sx={{ p: 0.25 }}
        >
          <ChevronLeftIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <IconButton
          size="small"
          disabled={nextDisabled}
          onClick={() => onPageChange(page + 1)}
          sx={{ p: 0.25 }}
        >
          <ChevronRightIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
};
